import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Package, MapPin, User, Users, ChevronLeft, Pencil } from 'lucide-react';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Button } from '../../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';
import AccessRestricted from '../../components/AccessRestricted';

interface IssuanceItem {
    id: string;
    inventoryItem: {
        id: string;
        name: string;
        sku: string;
        costPerUnit: number;
        currentStock: number;
        category: string;
    };
    quantity: number;
    unitCost: number;
    totalCost: number;
    expiryDate?: string;
}

interface Issuance {
    id: string;
    location: { id: string; name: string };
    workerType: 'System User' | 'Field Worker';
    systemUser?: { id: string; name: string };
    fieldWorker?: { id: string; name: string };
    notes: string;
    totalItems: number;
    totalValue: number;
    issuedBy: { id: string; name: string };
    issuedAt: string;
    items: IssuanceItem[];
    signatureType?: 'Draw' | 'Type';
    signatureData?: string;
}

const EditIssuance: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        locationId: '',
        workerType: 'Field Worker' as 'Field Worker' | 'System User',
        workerId: '',
        notes: ''
    });
    const [issuance, setIssuance] = useState<Issuance | null>(null);
    const [locations, setLocations] = useState<any[]>([]);
    const [fieldWorkers, setFieldWorkers] = useState<any[]>([]);
    const [systemUsers, setSystemUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [issuanceData, locs, workers, users] = await Promise.all([
                    api.get<Issuance>(`/api/inventory/issuances/${id}`),
                    api.get('/api/inventory/locations'),
                    api.get('/api/inventory/workers'),
                    api.get('/api/users?all_users=true')
                ]);
                setIssuance(issuanceData);
                setLocations(locs);
                setFieldWorkers(workers);
                setSystemUsers((users as any).users || users);

                setFormData({
                    locationId: issuanceData.location?.id || '',
                    workerType: issuanceData.workerType || 'Field Worker',
                    workerId: issuanceData.workerType === 'System User'
                        ? issuanceData.systemUser?.id || ''
                        : issuanceData.fieldWorker?.id || '',
                    notes: issuanceData.notes || ''
                });
            } catch (err) {
                console.error(err);
                toast.error('Failed to load issuance data');
                navigate('/inventory');
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchData();
    }, [id, navigate]);

    const handleSubmit = async () => {
        if (!formData.locationId || !formData.workerId) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setSaving(true);
            await api.patch(`/api/inventory/issuances/${id}`, formData);
            toast.success('Issuance updated successfully');
            navigate('/inventory');
        } catch (err: any) {
            toast.error(err.message || 'Failed to update issuance');
        } finally {
            setSaving(false);
        }
    };

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.INVENTORY_ISSUANCE)) {
        return <AccessRestricted navigateTo="/inventory" backMessage="Back to Inventory" />;
    }

    if (loading) {
        return (
            <div className="max-w-[1200px] mx-auto pb-20 flex items-center justify-center h-[50vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!issuance) {
        return (
            <div className="max-w-[1200px] mx-auto pb-20 flex items-center justify-center h-[50vh]">
                <p className="text-gray-500">Issuance not found</p>
            </div>
        );
    }

    return (
        <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/inventory')}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Issuance</h1>
                    <p className="text-gray-500 font-medium text-sm">Update issuance details for {issuance.workerType === 'System User' ? issuance.systemUser?.name : issuance.fieldWorker?.name}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form Area */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Issued Items (Read Only) */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-gray-50 rounded-xl text-gray-600">
                                <Package size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Issued Items</h3>
                                <p className="text-gray-500 text-sm font-medium">These items cannot be modified</p>
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4">
                            <div className="space-y-3">
                                {issuance.items?.map((item: any) => (
                                    <div key={item.id} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-white rounded-lg border border-gray-100 flex items-center justify-center">
                                                <Package size={14} className="text-gray-400" />
                                            </div>
                                            <div>
                                                <span className="font-bold text-gray-800 block">{item.inventoryItem?.name}</span>
                                                <span className="text-[10px] text-gray-400 font-medium">{item.inventoryItem?.sku}</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="font-black text-gray-900 block">x{item.quantity}</span>
                                            <span className="text-[10px] text-gray-400 font-medium">${Number(item.totalCost).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                <span className="text-xs font-bold text-gray-500">{issuance.totalItems} Items</span>
                                <span className="text-sm font-black text-gray-900">${Number(issuance.totalValue).toFixed(2)}</span>
                            </div>
                        </div>
                    </section>

                    {/* Editable Details */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                <Pencil size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Edit Details</h3>
                                <p className="text-gray-500 text-sm font-medium">Update the fields below</p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Location *</label>
                                <Select value={formData.locationId} onValueChange={(val) => setFormData({ ...formData, locationId: val })}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <div className="flex items-center gap-2">
                                            <MapPin size={14} className="text-gray-400" />
                                            <SelectValue placeholder="Select location" />
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Worker Type *</label>
                                    <Select
                                        value={formData.workerType}
                                        onValueChange={(val: any) => setFormData({ ...formData, workerType: val, workerId: '' })}
                                    >
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <div className="flex items-center gap-2">
                                                <User size={14} className="text-gray-400" />
                                                <SelectValue placeholder="Worker Type" />
                                            </div>
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl">
                                            <SelectItem value="Field Worker">Field Worker</SelectItem>
                                            <SelectItem value="System User">System User</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Worker *</label>
                                    <Select value={formData.workerId} onValueChange={(val) => setFormData({ ...formData, workerId: val })}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select worker..." />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl">
                                            {formData.workerType === 'Field Worker'
                                                ? fieldWorkers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)
                                                : systemUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)
                                            }
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Notes</label>
                                <textarea
                                    className="w-full h-24 rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all resize-none placeholder:text-gray-400"
                                    placeholder="Add any notes about this issuance..."
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </div>
                        </div>
                    </section>
                </div>

                {/* Sidebar / Actions */}
                <div className="lg:col-span-1">
                    <div className="sticky top-6 space-y-6">
                        <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="font-bold text-gray-900">Actions</h3>
                            <div className="space-y-3">
                                <Button
                                    className="w-full rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm gap-2 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={16} />
                                            Save Changes
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full rounded-2xl h-12 font-bold text-sm border-gray-100"
                                    onClick={() => navigate('/inventory')}
                                >
                                    Cancel
                                </Button>
                            </div>
                            <div className="pt-4 border-t border-gray-50 space-y-2">
                                <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                                    Issued by {issuance.issuedBy?.name}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                                    {new Date(issuance.issuedAt).toLocaleString()}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditIssuance;
